import { Component, HostListener, OnInit, inject } from '@angular/core';
import {Observable, ReplaySubject, Subject, zip} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {first, map, mergeMap} from 'rxjs/operators';
import {ClientService} from '../../../client/client.service';
import {Client} from '../../../client/client';
import {Prestatie} from '../../../prestatie/prestatie';
import {PrestatieService} from '../../../prestatie/prestatie.service';
import {DienstService} from '../../../dienst/dienst.service';
import {Dienst} from '../../../dienst/dienst';
import {Diensten} from '../../../dienst/diensten';
import UitgevoerdeTaak from '../../../prestatie/uitgevoerde-taak';
import {TaakGeschiedenis} from '../../../taakgeschiedenis/taak-geschiedenis';
import {TaakGeschiedenisService} from '../../../taakgeschiedenis/taak-geschiedenis.service';
import {TaakTipAantal} from '../../../tip/taak-tip-aantal';
import {TipService} from '../../../tip/tip.service';

@Component({
    templateUrl: './prestatie-taken.page.html',
    styleUrls: ['./prestatie-taken.page.scss'],
    standalone: false
})
export class PrestatieTakenPage implements OnInit {
  private prestatieService = inject(PrestatieService);
  private clientService = inject(ClientService);
  private taakGeschiedenisService = inject(TaakGeschiedenisService);
  private dienstService = inject(DienstService);
  private tipService = inject(TipService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  prestatie$: Subject<Prestatie> = new ReplaySubject(1);
  client$: Observable<Client>;
  taakGeschiedenis$: Observable<TaakGeschiedenis>;
  dienst$: Observable<Dienst>;
  taakTipAantallen$: Observable<Array<TaakTipAantal>>;

  @HostListener('window:focus') onFocus() {
    window.location.reload();
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(
      map(paramMap => paramMap.get('id')),
      mergeMap(id => this.prestatieService.getPrestatie$(id)),
    ).subscribe(prestatie => this.prestatie$.next(prestatie));
    this.client$ = this.prestatie$.pipe(
      first(),
      mergeMap(prestatie => this.clientService.getClientById$(prestatie.getClientId()))
    );
    this.taakGeschiedenis$ = this.prestatie$.pipe(
      first(),
      mergeMap(prestatie => this.taakGeschiedenisService.getTaakGeschiedenis$(prestatie.getClientId()))
    );
    this.dienst$ = zip(
      this.route.paramMap.pipe(map(paramMap => paramMap.get('dienstId'))),
      this.dienstService.getDiensten$(),
      this.client$,
    )
      .pipe(map(([dienstId, diensten, client]: [string, Diensten, Client]) => diensten.filterForClientDienst(client.getDienst(dienstId))));
    this.taakTipAantallen$ = this.client$.pipe(
      first(),
      mergeMap(client => this.tipService.getTaakTipAantallen(client.getId()))
    );
  }

  onDienstenClicked(prestatie: Prestatie): void {
    this.router.navigateByUrl(`/prestatie/${prestatie.getId()}/diensten`);
  }

  onStopClicked(prestatie: Prestatie): void {
    this.router.navigateByUrl(`/prestatie/${prestatie.getId()}/stop`);
  }

  onMeldingenClicked(prestatie: Prestatie, client: Client, dienst: Dienst): void {
    this.router.navigate([`/prestatie/${prestatie.getId()}/meldingen/${client.getId()}`], {queryParams: {dienstId: dienst.getId()}});
  }

  onSignaalkaartenClicked(prestatie: Prestatie, client: Client, dienst: Dienst): void {
    this.router.navigate([`/prestatie/${prestatie.getId()}/signaalkaarten/${client.getId()}`], {queryParams: {dienstId: dienst.getId()}});
  }

  onClientFicheClicked(client: Client, dienst: Dienst): void {
    this.router.navigate([`/clienten/${client.getId()}/fiche`], {queryParams: {dienstId: dienst.getId()}});
  }

  onTipsOpened(client: Client, [dienstId, categorieId, taakId]: [string, string, string]): void {
    this.router.navigateByUrl(`/clienten/${client.getId()}/tips/dienst/${dienstId}/categorie/${categorieId}/taak/${taakId}`);
  }

  onTaakSelect(prestatie: Prestatie, uitgevoerdeTaak: UitgevoerdeTaak): void {
    this.prestatieService.selectTaak(prestatie.getId(), uitgevoerdeTaak)
      .subscribe(newPrestatie => this.prestatie$.next(newPrestatie));
  }

  onTaakDeselect(prestatie: Prestatie, uitgevoerdeTaak: UitgevoerdeTaak): void {
    this.prestatieService.deselectTaak(prestatie.getId(), uitgevoerdeTaak)
      .subscribe(newPrestatie => this.prestatie$.next(newPrestatie));
  }

}
